// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { contactDetails, examPaperState, routes } from "../../config/constant";
import { RootState, useAppDispatch, useAppSelector } from "../../slices/store";
import {
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Stack } from "@mui/system";
import { RatingForm } from "./RatingForm";
import { submitAnswerSheet } from "../../slices/examSlice/submitAnswerSheet";
import { LoadingButton } from "@mui/lab";
import { setOngoingExamStatus } from "../../slices/examSlice/ongoingExam";
import { useEffect } from "react";
import { AreasOfImprovement } from "./AreasOfImprovement";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  ".MuiBackdrop-root": {
    backdropFilter: "blur(10px)",
    backgroundColor: "rgba(0,0,30,0.4)",
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

export default function McqSubmissionDialog(props: { snapshot: any, setConflict: (value: boolean) => void }) {
  const examState = useAppSelector((state: RootState) => state.selectedExam);
  const contactState = useAppSelector((state: RootState) => state.contact);
  const resultSheet = useAppSelector((state: RootState) => state.examResults);
  const ongoingExamState = useAppSelector(
    (state: RootState) => state.ongoingExam
  );
  let navigate = useNavigate();
  const dispatch = useAppDispatch();

  const selectedExam = examState.response[0];
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (ongoingExamState.state === "completed") {
      submitExam();
    }
  }, [ongoingExamState.state]);

  const submitExam = async () => {
    props.snapshot.paperName = `${
      selectedExam?.examVisibleType
        ? selectedExam?.examVisibleType
        : selectedExam?.examType
    }`;

    var payload = {
      examId: selectedExam?.id,
      answers: props.snapshot,
      isPaperInProgress: false,
    };
    try {
      await dispatch(submitAnswerSheet(payload)).unwrap();
    } catch (error: any) {
      if (error.message === "Request failed with status code 409") {
        setOpen(true);
        return;
      }
      console.error("Error occurred while submitting the exam", error);
    }
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    navigate(routes.exams);
  };
  return (
    <div>
      <LoadingButton
        disabled={!ongoingExamState.isAllAnswered}
        loading={resultSheet.state === "loading"}
        sx={{ my: 2 }}
        variant="contained"
        onClick={submitExam}
        translate="no"
      >
        Submit Exam
      </LoadingButton>
      {resultSheet.state === "success" && (
        <BootstrapDialog
          aria-labelledby="customized-dialog-title"
          open={open}
          maxWidth={"md"}
        >
          <BootstrapDialogTitle
            id="customized-dialog-title"
            onClose={handleClose}
          >
            {`${
              selectedExam?.examVisibleType
                ? selectedExam?.examVisibleType
                : selectedExam?.examType
            } - ${selectedExam?.examPaperType}`}
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <Box sx={{ m: 3 }}>
              <Stack
                alignItems={"center"}
                justifyContent={"center"}
                direction="row"
                spacing={2}
                sx={{ mb: 3 }}
              >
                {/* <CheckCircleIcon
                sx={{ color: "secondary.main", fontSize: "30px" }}
              /> */}
                <Typography
                  sx={{ fontWeight: "bold", mb: 1 }}
                  gutterBottom
                  color={"secondary.main"}
                  variant="h6"
                >
                  WSO2 ONLINE CERTIFICATION EXAM
                </Typography>
              </Stack>
              <Stack justifyContent={"center"} alignItems={"center"} mb={4}>
                <Card
                  variant="outlined"
                  sx={{ p: 2, width: "70%", textAlign: "center" }}
                >
                  <CardContent>
                    <Typography fontWeight={"bold"}>
                      {` Exam Results for
                    ${contactState?.response.firstName}
                    ${contactState?.response.lastName}`}
                    </Typography>
                    <Typography variant="subtitle2">
                      {contactState?.response.email}
                    </Typography>
                    <Stack
                      justifyContent={"center"}
                      alignItems={"center"}
                      sx={{ mt: 3 }}
                    >
                      <List sx={{ width: "50%"}}>
                        <ListItem>
                          <ListItemText>Correct Answers</ListItemText>
                          <Typography fontWeight={"bold"}>
                            {resultSheet.response?.correctAnswers}/
                            {resultSheet.response?.numberOfAnswers}
                          </Typography>
                        </ListItem>
                        <ListItem>
                          <ListItemText>Your Score</ListItemText>
                          <Typography fontWeight={"bold"}>
                            {resultSheet.response?.score}%
                          </Typography>
                        </ListItem>
                        <ListItem>
                          <ListItemText>Passing Score</ListItemText>
                          <Typography fontWeight={"bold"}>
                            {resultSheet.response?.passMark}%
                          </Typography>
                        </ListItem>
                      </List>
                      <Stack>
                        {resultSheet.response?.areasOfImprovement && (
                          <AreasOfImprovement
                              data={resultSheet.response.areasOfImprovement} />
                        )}
                      </Stack>
                    </Stack>
                    {resultSheet.response?.paperState ==
                    examPaperState.passed ? (
                      <>
                        <Typography
                          fontWeight={"bold"}
                          color="green"
                          variant="h6"
                        >
                          Congratulations, you have passed this exam!
                        </Typography>
                        {/* // TODO : Clarification - is this button necessary? */}
                        {/* <Button
                          size="small"
                          onClick={() => {
                            navigate(routes.certifications);
                          }}
                          variant="contained"
                          sx={{ px: 1, mt: 2, fontSize: "12px" }}
                        >
                          View your Certificate
                        </Button> */}
                      </>
                    ) : (
                      <>
                        <Typography
                          fontWeight={"bold"}
                          color="red"
                          variant="h6"
                        >
                          Unfortunately, you did not achieve a passing grade.
                        </Typography>
                        <Typography
                          pt={2}
                          color="text.secondary"
                          variant="subtitle2"
                        >
                          Please feel free to contact{" "}
                          <a
                            style={{
                              textDecoration: "none",
                              color: "text.secondary",
                            }}
                            href={contactDetails.mailTo}
                          >
                            {" "}
                            certification@wso2.com
                          </a>{" "}
                          if you have any questions.
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Stack>
              <ul style={{ marginBottom: "30px" }}>
                <li>
                  <Typography variant="subtitle2">
                    Recordings should be sent within 24 hours of attempting the
                    exam regardless of passing or failing. If the recording is
                    not received within the allotted time, the candidate will be
                    awarded a failing grade or barred from attempting any WSO2
                    certifications.
                  </Typography>
                </li>
                <li>
                  <Typography variant="subtitle2">
                    Screen recordings must not be shared with anyone except with
                    WSO2.
                  </Typography>
                </li>
              </ul>

              <RatingForm
                certificationType={
                  selectedExam?.examVisibleType
                    ? selectedExam?.examVisibleType
                    : selectedExam?.examType
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ px: 1, color: "secondary.main" }}
              autoFocus
              onClick={handleClose}
            >
              Done
            </Button>
          </DialogActions>
        </BootstrapDialog>
      )}
    </div>
  );
}
